// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import Button from 'shared/components/common/Button';
import Icon from 'shared/components/common/Icon';
import GenerateJobsFromOrderMutation from 'main-app/mutations/GenerateJobsFromOrder';
import GenerateAllPendingJobsMutation from 'main-app/mutations/GenerateAllPendingJobs';
import * as Actions from 'main-app/store/Actions';

type Props = {
  history: RouterHistory,
};

const ContentWrapper = styled.div`
  flex: 1;
  padding: 24px;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  background: #2196f3;
  border: 1px solid #2196f3;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 12px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1976d2;
    border-color: #1976d2;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #ccc;
    border-color: #ccc;
    color: #666;
    cursor: not-allowed;
    transform: none;
  }
`;

class PendingOrders extends PureComponent<Props> {
  handleGenerateForOrder = (orderId: string, orderNumber: string) => {
    const confirmMessage = i18n.t(
      `Generate job tickets for Order #${orderNumber}?`
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    Actions.alertNotification(
      i18n.t('Generating job tickets...'),
      'info'
    );

    GenerateJobsFromOrderMutation.commit(
      { orderId, skipMaterialCheck: false },
      {
        onSuccess: response => {
          const result = response?.generateJobsFromOrder?.result;
          const count = result?.success || 0;
          const errors = result?.failed || 0;
          const skipped = result?.skipped || 0;
          
          if (count > 0) {
            const message = errors > 0 || skipped > 0
              ? i18n.t(`Generated ${count} job(s). ${errors} error(s), ${skipped} skipped`)
              : i18n.t(`Successfully generated ${count} job ticket(s)!`);
            
            Actions.alertNotification(message, errors > 0 ? 'warning' : 'success');
            
            // Refresh the page to show updated data
            window.location.reload();
          } else if (errors > 0) {
            const firstLog = result.logs?.find(log => log.errorMessage);
            Actions.alertNotification(
              i18n.t(`Failed to generate jobs: ${firstLog?.errorMessage || 'Unknown error'}`),
              'error'
            );
          } else {
            Actions.alertNotification(
              i18n.t('No jobs were generated. All yields may already have jobs.'),
              'warning'
            );
          }
        },
        onFailure: error => {
          Actions.alertNotification(
            i18n.t(`Failed to generate jobs: ${error.message || 'Unknown error'}`),
            'error'
          );
        },
      }
    );
  };

  handleGenerateAll = (pendingOrders: Array<any>) => {
    if (!pendingOrders || pendingOrders.length === 0) {
      Actions.alertNotification(
        i18n.t('No pending orders to generate jobs for'),
        'warning'
      );
      return;
    }

    const totalPending = pendingOrders.reduce((sum, order) => sum + order.pendingCount, 0);
    const confirmMessage = i18n.t(
      `This will generate ${totalPending} job ticket(s) from ${pendingOrders.length} order(s). Continue?`
    );

    if (!window.confirm(confirmMessage)) {
      return;
    }

    Actions.alertNotification(
      i18n.t('Generating job tickets...'),
      'info'
    );

    GenerateAllPendingJobsMutation.commit(
      {},
      {
        onSuccess: response => {
          const result = response?.generateAllPendingJobs?.result;
          const count = result?.success || 0;
          const errors = result?.failed || 0;
          const skipped = result?.skipped || 0;
          
          if (count > 0) {
            const message = errors > 0 || skipped > 0
              ? i18n.t(`Generated ${count} job(s). ${errors} error(s), ${skipped} skipped`)
              : i18n.t(`Successfully generated ${count} job ticket(s)!`);
            
            Actions.alertNotification(message, errors > 0 ? 'warning' : 'success');
            
            // Refresh the page to show updated data
            window.location.reload();
          } else {
            Actions.alertNotification(
              i18n.t('No jobs were generated. Check that items have default workflows assigned.'),
              'warning'
            );
          }
        },
        onFailure: error => {
          Actions.alertNotification(
            i18n.t(`Failed to generate jobs: ${error.message || 'Unknown error'}`),
            'error'
          );
        },
      }
    );
  };

  render() {
    const { history } = this.props;

    return (
      <Page>
        <QueryRenderer
          environment={relayEnvironment}
          query={graphql`
            query PendingOrdersQuery {
              ordersPendingJobGeneration {
                order {
                  id
                  orderNumber
                  customer {
                    id
                    name
                  }
                }
                pendingCount
              }
            }
          `}
          render={({ props, error }) => {
            if (error) {
              return (
                <>
                  <PageHeaderList
                    title={i18n.t('Pending Job Generation')}
                    subtitle={i18n.t('Orders awaiting job ticket creation')}
                  />
                  <ContentWrapper>
                    <EmptyState>
                      {i18n.t('Error loading pending orders')}
                    </EmptyState>
                  </ContentWrapper>
                </>
              );
            }

            if (!props) {
              return <Loader />;
            }

            const { ordersPendingJobGeneration } = props;
            const hasPending = ordersPendingJobGeneration && ordersPendingJobGeneration.length > 0;
            


            return (
              <>
                <PageHeaderList
                  title={i18n.t('Pending Job Generation')}
                  subtitle={i18n.t('Orders awaiting job ticket creation')}
                  actions={
                    hasPending && (
                      <HeaderActions>
                        <Button
                          buttonType="primary"
                          onClick={() => this.handleGenerateAll(ordersPendingJobGeneration)}
                        >
                          {i18n.t('Generate All Jobs')}
                        </Button>
                      </HeaderActions>
                    )
                  }
                />

                <ContentWrapper>
                  {!hasPending ? (
                    <EmptyState>
                      <Icon type="circle-check-green" size={48} style={{ marginBottom: 16 }} />
                      <div>{i18n.t('No pending orders')}</div>
                      <div style={{ fontSize: 14, marginTop: 8 }}>
                        {i18n.t('All orders have job tickets created')}
                      </div>
                    </EmptyState>
                  ) : (
                        <Table
                          columns={[
                            {
                              Header: i18n.t('Order #'),
                              accessor: 'order.orderNumber',
                              width: 150,
                            },
                            {
                              Header: i18n.t('Customer'),
                              accessor: 'order.customer.name',
                              width: 200,
                            },
                            {
                              Header: i18n.t('Pending Jobs'),
                              accessor: 'pendingCount',
                              width: 120,
                              headerStyle: { textAlign: 'center' },
                              style: { textAlign: 'center', color: '#2196f3', fontWeight: 'bold' },
                            },
                            {
                              Header: i18n.t('Actions'),
                              accessor: 'actions',
                              width: 100,
                              headerStyle: { textAlign: 'center' },
                              style: { textAlign: 'center' },
                              Cell: ({ row }) => (
                                <ActionButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const orderData = row._original;
                                    this.handleGenerateForOrder(orderData.order.id, orderData.order.orderNumber);
                                  }}
                                >
                                  {i18n.t('Generate')}
                                </ActionButton>
                              ),
                            },
                          ]}
                          data={ordersPendingJobGeneration}
                          onRowClick={(row) => history.push(`/order/${row.order.id}`)}
                        />
                  )}
                </ContentWrapper>
              </>
            );
          }}
        />
      </Page>
    );
  }
}

export default PendingOrders;
